/**
 * La lettera di compensazione da mandare alla controparte.
 *
 * Sta in una pagina sua, fuori dal menu e dalle schede: quello che si stampa
 * deve essere la lettera e basta. Non c'è nessuna libreria PDF nel progetto e
 * non ne serve una — il browser stampa in PDF meglio di quanto lo farebbe un
 * generatore lato server, e per giunta con i caratteri giusti.
 *
 * Il testo dichiara una compensazione volontaria: le due parti si accordano per
 * estinguere i reciproci crediti fino a concorrenza del minore. Non è una
 * comunicazione di cortesia — è la traccia scritta dell'accordo, ed è la
 * ragione per cui la data che porta è quella dell'accordo e non quella di
 * stampa.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import { Caricamento, Errore, useCarico } from '../componenti/base';
import { formattaData, formattaEuro } from '../formato';
import { useSessione } from '../main';

interface Parte {
  ragioneSociale?: string;
  denominazione?: string;
  partitaIva: string | null;
  codiceFiscale?: string | null;
  indirizzo: string | null;
  cap: string | null;
  comune: string | null;
  provincia: string | null;
  pec: string | null;
}

interface DocumentoLettera {
  numero: string;
  tipo: 'ATTIVA' | 'PASSIVA';
  notaDiCredito: boolean;
  dataCompetenza: string;
  totaleCent: number;
  residuoCent: number;
}

/**
 * Come chiamare un documento nella lettera. «Nostra fattura» su una nota di
 * credito sarebbe sbagliato, e la controparte se ne accorge.
 */
function comeSiChiama(d: DocumentoLettera, nostro: boolean): string {
  const chi = nostro ? 'Nostra' : 'Vostra';
  return d.notaDiCredito ? `${chi} nota di credito n. ${d.numero}` : `${chi} fattura n. ${d.numero}`;
}

interface Lettera {
  compensazione: { id: string; importoCent: number; data: string; nota: string };
  /** Ciò che la controparte deve a noi: una fattura attiva o una nota di credito ricevuta. */
  credito: DocumentoLettera | null;
  /** Ciò che noi dobbiamo a lei: una fattura passiva o una nota di credito emessa. */
  debito: DocumentoLettera | null;
  azienda: Parte | null;
  controparte: Parte | null;
}

function Indirizzo({ p }: { p: Parte | null }) {
  if (!p) return null;
  const riga = [p.cap, p.comune, p.provincia && `(${p.provincia})`].filter(Boolean).join(' ');
  return (
    <div className="text-sm leading-relaxed">
      <div className="font-semibold">{p.ragioneSociale ?? p.denominazione}</div>
      {p.indirizzo && <div>{p.indirizzo}</div>}
      {riga && <div>{riga}</div>}
      {/* Chi non ha partita IVA — società semplici, associazioni, persone
          fisiche — sulla lettera va identificato col codice fiscale, altrimenti
          il destinatario resta senza nessun riferimento fiscale. */}
      {p.partitaIva ? (
        <div>P.IVA {p.partitaIva}</div>
      ) : (
        p.codiceFiscale && <div>C.F. {p.codiceFiscale}</div>
      )}
      {p.pec && <div>{p.pec}</div>}
    </div>
  );
}

export function LetteraCompensazione() {
  const { id } = useParams<{ id: string }>();
  const { organizzazione } = useSessione();
  const { dati, caricamento, errore, ricarica } = useCarico(
    () => api.get<Lettera>(`/api/compensazioni/${id}/lettera`),
    [id],
  );

  // Il titolo della finestra diventa il nome del PDF quando si stampa: senza,
  // il file si chiamerebbe come l'applicazione e sarebbero tutti uguali.
  useEffect(() => {
    if (!dati) return;
    const precedente = document.title;
    document.title = `Compensazione ${dati.credito?.numero ?? ''} / ${dati.debito?.numero ?? ''}`;
    return () => {
      document.title = precedente;
    };
  }, [dati]);

  if (caricamento) return <Caricamento />;
  if (errore || !dati) return <div className="p-6"><Errore testo={errore ?? 'Compensazione non trovata'} riprova={ricarica} /></div>;

  const { compensazione: c, credito, debito, azienda, controparte } = dati;
  const f = organizzazione.formatoData;
  const luogo = azienda?.comune ? `${azienda.comune}, ` : '';

  return (
    // Sul foglio non ci va lo sfondo dell’applicazione né il margine dello
    // schermo: il margine lo mette la stampante.
    <div className="mx-auto max-w-[820px] p-8 print:max-w-none print:bg-white print:p-0">
      {/* Fuori dalla stampa: i comandi non finiscono sul foglio. */}
      <div className="mb-6 flex justify-end gap-2 print:hidden">
        <button className="bottone-primario" onClick={() => window.print()}>
          Stampa o salva in PDF
        </button>
      </div>

      <article className="bg-white p-10 text-inchiostro-900 shadow-sm print:p-0 print:shadow-none">
        <div className="flex justify-between gap-8">
          <Indirizzo p={azienda} />
          <div className="max-w-[46%]">
            <div className="mb-1 text-xs uppercase tracking-wide text-inchiostro-500">Spettabile</div>
            <Indirizzo p={controparte} />
          </div>
        </div>

        <div className="mt-10 text-sm">
          {luogo}
          {formattaData(c.data, f)}
        </div>

        <h1 className="mt-8 text-base font-semibold">
          Oggetto: compensazione volontaria di crediti e debiti reciproci
        </h1>

        <div className="mt-6 space-y-4 text-sm leading-relaxed">
          <p>
            Con la presente si dà atto dell’accordo intercorso fra le parti per la compensazione dei
            rispettivi crediti, ai sensi degli articoli 1252 e seguenti del Codice Civile, fino a concorrenza
            del minore di essi.
          </p>

          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-inchiostro-300 text-left">
                <th className="py-2 font-medium">Documento</th>
                <th className="py-2 font-medium">Data</th>
                <th className="py-2 text-right font-medium">Importo</th>
              </tr>
            </thead>
            <tbody>
              {credito && (
                <tr className="border-b border-inchiostro-200">
                  {/* Un credito verso di noi è una nostra fattura, oppure una
                      nota di credito che la controparte ci ha emesso. */}
                  <td className="py-2">{comeSiChiama(credito, !credito.notaDiCredito)}</td>
                  <td className="py-2">{formattaData(credito.dataCompetenza, f)}</td>
                  <td className="py-2 text-right num">{formattaEuro(credito.totaleCent)}</td>
                </tr>
              )}
              {debito && (
                <tr className="border-b border-inchiostro-200">
                  <td className="py-2">{comeSiChiama(debito, debito.notaDiCredito)}</td>
                  <td className="py-2">{formattaData(debito.dataCompetenza, f)}</td>
                  <td className="py-2 text-right num">{formattaEuro(debito.totaleCent)}</td>
                </tr>
              )}
              <tr className="font-semibold">
                <td className="py-2" colSpan={2}>
                  Importo compensato
                </td>
                <td className="py-2 text-right num">{formattaEuro(c.importoCent)}</td>
              </tr>
            </tbody>
          </table>

          {/* Cosa resta aperto è la parte che evita il giro di telefonate: una
              compensazione parziale lascia un saldo, e chi legge deve sapere
              da che parte sta. */}
          {(credito?.residuoCent || debito?.residuoCent) ? (
            <p>
              A seguito della compensazione residuano:{' '}
              {credito && credito.residuoCent > 0 && (
                <>
                  <strong>{formattaEuro(credito.residuoCent)}</strong> a Vostro debito sul documento n.{' '}
                  {credito.numero}
                </>
              )}
              {credito && credito.residuoCent > 0 && debito && debito.residuoCent > 0 && '; '}
              {debito && debito.residuoCent > 0 && (
                <>
                  <strong>{formattaEuro(debito.residuoCent)}</strong> a nostro debito sul documento n.{' '}
                  {debito.numero}
                </>
              )}
              .
            </p>
          ) : (
            <p>
              A seguito della compensazione nulla residua fra le parti in relazione ai documenti sopra
              indicati.
            </p>
          )}

          <p>{c.nota}</p>

          <p>
            Vi preghiamo di voler confermare per accettazione, restituendo copia della presente sottoscritta
            per benestare.
          </p>

          <p className="pt-4">Distinti saluti.</p>
        </div>

        <div className="mt-16 flex justify-between text-sm">
          <div>
            <div className="mb-12 text-inchiostro-500">Per {azienda?.ragioneSociale ?? 'la scrivente'}</div>
            <div className="w-56 border-t border-inchiostro-400 pt-1 text-xs text-inchiostro-500">Firma</div>
          </div>
          <div>
            <div className="mb-12 text-inchiostro-500">Per accettazione</div>
            <div className="w-56 border-t border-inchiostro-400 pt-1 text-xs text-inchiostro-500">Firma</div>
          </div>
        </div>
      </article>
    </div>
  );
}
